import React, { useState, useEffect, useMemo } from 'react';
import { useWishlist } from '../Context/WishlistContext';
import { useNavigate } from 'react-router-dom';
import Navbar from '../Components/Navbar';
import Footer from '../Components/Footer';
import Scrollfunctions from '../Components/Scrollfunctions';
import { Heart, Share2, Trash2, ExternalLink, Check } from 'lucide-react';

// Import all data sources to look up items
import { walldata as wallcoveringViewAll } from '../Config/wallcoveringviewalldata';
import { walldata as artprintsViewAll } from '../Config/artprintsviewalldata';
import { walldata as decorativeData } from '../Config/wallcoveringnaturedata';
import { walldata as abstractWallcoveringData } from '../Config/wallcoveringabstractdata';
import { walldata as corporateData } from '../Config/wallcoveringcorporatedata';
import { walldata as kidsData } from '../Config/wallcoveringkidsdata';
import { walldata as minimalPatternData } from '../Config/wallcoveringminimalandpatterndata';
import { walldata as artprintsAbstractData } from '../Config/artprintsabstractdata';
import { walldata as artprintsDecorativeData } from '../Config/artprintsdecorativedata';
import { walldata as artprintsExclusiveData } from '../Config/artprintsexclusivedata';
import { walldata as artprintsLandscapeData } from '../Config/artprintslandscapedata';
import { walldata as artprintsStData } from '../Config/artprintsstdata';
import { walldatas as exclusiveHandData } from '../Config/exclusivehandwallcoveringdatas';

const Wishlist = () => {
  const { wishlistItems, removeFromWishlist, getShareableUrl, clearWishlist, addToWishlist } = useWishlist();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [copied, setCopied] = useState(false);

  // Also read from localStorage as fallback
  const getItemsFromLocalStorage = () => {
    try {
      const savedItems = JSON.parse(localStorage.getItem('wishlist') || '[]');
      return savedItems;
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return [];
    }
  };

  // Sync context with localStorage on mount (in case they're out of sync)
  useEffect(() => {
    const savedItems = getItemsFromLocalStorage();
    const savedTitles = savedItems.map(item => item.title).filter(Boolean);
    
    // If localStorage has items that aren't in context, add them
    savedTitles.forEach(title => {
      if (!wishlistItems.includes(title)) {
        const savedItem = savedItems.find(item => item.title === title);
        if (savedItem) {
          console.log('Syncing item from localStorage to context:', title);
          addToWishlist(savedItem);
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount - intentionally exclude addToWishlist and wishlistItems

  // Combine all data sources (memoized to avoid recreating on every render)
  const allDataSources = useMemo(() => [
    ...wallcoveringViewAll,
    ...artprintsViewAll,
    ...decorativeData,
    ...abstractWallcoveringData,
    ...corporateData,
    ...kidsData,
    ...minimalPatternData,
    ...artprintsAbstractData,
    ...artprintsDecorativeData,
    ...artprintsExclusiveData,
    ...artprintsLandscapeData,
    ...artprintsStData,
    ...exclusiveHandData,
  ], []);

  // Find items by title
  useEffect(() => {
    console.log('Wishlist page - wishlistItems from context:', wishlistItems);
    console.log('Wishlist page - allDataSources length:', allDataSources.length);
    
    // Priority: URL params > context > localStorage
    // This ensures shared URLs work in any browser
    const urlParams = new URLSearchParams(window.location.search);
    const urlItems = urlParams.get('items') || urlParams.get('w');
    
    let itemsToProcess = [];
    
    // First, check URL (highest priority for shared wishlists)
    if (urlItems && urlItems.split(',').filter(Boolean).length > 0) {
      try {
        // Decode URL-encoded items
        const decodedItems = decodeURIComponent(urlItems);
        const urlItemsArray = decodedItems.split(',').filter(Boolean);
        itemsToProcess = urlItemsArray;
        console.log('Wishlist page - loaded from URL (shared wishlist):', itemsToProcess);
      } catch (error) {
        console.error('Error decoding URL items:', error);
        // Fallback to non-decoded
        const urlItemsArray = urlItems.split(',').filter(Boolean);
        itemsToProcess = urlItemsArray;
      }
    } 
    // Second, check context
    else if (wishlistItems.length > 0) {
      itemsToProcess = wishlistItems;
      console.log('Wishlist page - loaded from context:', itemsToProcess);
    }
    // Third, fallback to localStorage
    else {
      const localStorageItems = getItemsFromLocalStorage();
      itemsToProcess = localStorageItems.map(item => item.title).filter(Boolean);
      console.log('Wishlist page - loaded from localStorage:', itemsToProcess);
    }
    
    console.log('Wishlist page - final itemsToProcess:', itemsToProcess);
    
    const foundItems = itemsToProcess
      .map(itemId => {
        // Parse itemId - may include selections (format: "title::base64selections")
        let title = itemId;
        let selections = null;
        
        if (itemId.includes('::')) {
          const parts = itemId.split('::');
          title = parts[0];
          try {
            selections = JSON.parse(atob(parts[1]));
          } catch (error) {
            console.error('Error parsing selections:', error);
          }
        }
        
        // Priority 1: Try to find in localStorage first (preserves exact image that was added)
        // This ensures we show the same image that was displayed when the item was added
        const savedItems = getItemsFromLocalStorage();
        let item = null;
        let savedItem = savedItems.find(i => 
          (i.wishlistId === itemId) || 
          (i.wishlistId && i.wishlistId === itemId) ||
          (i.title === title && !i.wishlistId && !itemId.includes('::'))
        );
        
        if (savedItem) {
          console.log('Found item in localStorage (using saved image):', savedItem);
          // Use the saved item which has the exact image that was displayed
          item = {
            ...savedItem,
            // Ensure all image properties are preserved
            Img: savedItem.Img || savedItem.ImgJpg || savedItem.ImgJpeg || '',
            ImgJpg: savedItem.ImgJpg || savedItem.Img || savedItem.ImgJpeg || '',
            ImgJpeg: savedItem.ImgJpeg || savedItem.Img || savedItem.ImgJpg || '',
            desc: savedItem.desc || savedItem.series || savedItem.title || savedItem.code || title,
            context: savedItem.context || savedItem.artist || '',
            code: savedItem.code || savedItem.title,
            selections: savedItem.selections || selections, // Use saved selections or parsed from URL
            sourceRoute: savedItem.sourceRoute || null, // Preserve sourceRoute
          };
        } else {
          // Priority 2: Try to find in data sources (fallback)
          // Search in specific data sources based on title prefix for better accuracy
          console.log('Item not found in localStorage, trying data sources:', title);
          
          // Try to find in specific data sources first (more accurate)
          let foundItem = null;
          if (title.startsWith('#AW')) {
            foundItem = abstractWallcoveringData.find(i => i.title === title);
          } else if (title.startsWith('#AB')) {
            foundItem = artprintsAbstractData.find(i => i.title === title);
          } else if (title.startsWith('#CR')) {
            foundItem = corporateData.find(i => i.title === title);
          } else if (title.startsWith('#KD')) {
            foundItem = kidsData.find(i => i.title === title);
          } else if (title.startsWith('#DC')) {
            // Check both decorative sources
            foundItem = artprintsDecorativeData.find(i => i.title === title) || 
                       decorativeData.find(i => i.title === title);
          } else if (title.startsWith('#EX')) {
            foundItem = artprintsExclusiveData.find(i => i.title === title);
          } else if (title.startsWith('#LS')) {
            foundItem = artprintsLandscapeData.find(i => i.title === title);
          } else if (title.startsWith('#ST')) {
            foundItem = artprintsStData.find(i => i.title === title);
          } else if (title.startsWith('#EHP')) {
            foundItem = exclusiveHandData.find(i => (i.code || i.title) === title);
          }
          
          // If not found in specific sources, try allDataSources as fallback
          if (!foundItem) {
            foundItem = allDataSources.find(i => i.title === title || i.code === title);
          }
          
          if (foundItem) {
            console.log('Found item in data sources:', foundItem);
            // Ensure image properties are set - preserve all available image URLs
            // Note: Some items have Img (which may be jpeg), ImgJpg, and sometimes ImgJpeg
            // Preserve all original properties and add fallbacks
            item = {
              ...foundItem,
              // Preserve original image properties exactly as they are
              Img: foundItem.Img || '',
              ImgJpg: foundItem.ImgJpg || '',
              ImgJpeg: foundItem.ImgJpeg || (foundItem.Img && foundItem.Img.includes('.jpeg') ? foundItem.Img : '') || '',
              // Ensure we have at least one image URL for display
              // If Img is jpeg, use it; if it's jpg, use ImgJpg; otherwise use what's available
              desc: foundItem.desc || foundItem.series || foundItem.title || title,
              context: foundItem.context || foundItem.artist || '',
              code: foundItem.code || foundItem.title,
              selections: selections, // Use selections parsed from URL
              sourceRoute: null, // No sourceRoute for items loaded from URL
            };
            
            // Log image URLs for debugging
            console.log('Item image URLs:', {
              title: item.title,
              Img: item.Img,
              ImgJpg: item.ImgJpg,
              ImgJpeg: item.ImgJpeg,
              'Has any image': !!(item.Img || item.ImgJpg || item.ImgJpeg)
            });
            
            console.log('Item with images:', {
              title: item.title,
              Img: item.Img,
              ImgJpg: item.ImgJpg,
              ImgJpeg: item.ImgJpeg
            });
            
            // Save to localStorage so images persist for future loads
            try {
              const savedItems = JSON.parse(localStorage.getItem('wishlist') || '[]');
              const existingItem = savedItems.find(i => (i.wishlistId || i.title) === itemId);
              if (!existingItem) {
                savedItems.push({
                  ...item,
                  wishlistId: itemId,
                });
                localStorage.setItem('wishlist', JSON.stringify(savedItems));
                console.log('Saved item from data sources to localStorage:', item.title);
              }
            } catch (error) {
              console.error('Error saving item to localStorage:', error);
            }
          } else {
            console.error('Item not found in any data source:', title);
          }
        }
        
        if (!item) {
          console.error('Item not found anywhere:', title);
          return null;
        }
        
        // Determine route based on title prefix
        // Priority: 1) Specific category data (for items with category prefixes), 2) View All data
        let route = '';
        let routeIndex = -1;
        
        // First, check specific category data (for items with category prefixes)
        // This ensures items added from category pages go to the correct category route
        if (title.startsWith('#DC')) {
          // Check if item has a stored sourceRoute (preferred - most accurate)
          if (item.sourceRoute && item.sourceRoute.includes('/desc/')) {
            // Use the stored source route - this is the exact page where the item was added
            route = item.sourceRoute;
            const routeMatch = item.sourceRoute.match(/\/desc\/(\d+)/);
            if (routeMatch) {
              routeIndex = parseInt(routeMatch[1]) - 1;
            }
            console.log('Using stored sourceRoute for #DC item:', route, 'from item:', item);
          } else {
            // Fallback: Check both data sources, but prioritize artprints if item is in both
            const artprintsIndex = artprintsDecorativeData.findIndex(i => i.title === title);
            const wallcoveringIndex = decorativeData.findIndex(i => i.title === title);
            
            // If found in artprints, use that (artprints decorative pages are more common)
            if (artprintsIndex !== -1) {
              routeIndex = artprintsIndex;
              route = `/artprints/decorative/desc/${routeIndex + 1}`;
            } else if (wallcoveringIndex !== -1) {
              routeIndex = wallcoveringIndex;
              route = `/wallcoverings/decorative/desc/${routeIndex + 1}`;
            }
          }
        } else if (title.startsWith('#AW')) {
          routeIndex = abstractWallcoveringData.findIndex(i => i.title === title);
          if (routeIndex !== -1) {
            route = `/wallcoverings/abstract/desc/${routeIndex + 1}`;
          }
        } else if (title.startsWith('#AB')) {
          routeIndex = artprintsAbstractData.findIndex(i => i.title === title);
          if (routeIndex !== -1) {
            route = `/artprints/abstract/desc/${routeIndex + 1}`;
          }
        } else if (title.startsWith('#CR')) {
          routeIndex = corporateData.findIndex(i => i.title === title);
          if (routeIndex !== -1) {
            route = `/wallcoverings/corporate/desc/${routeIndex + 1}`;
          }
        } else if (title.startsWith('#KD')) {
          routeIndex = kidsData.findIndex(i => i.title === title);
          if (routeIndex !== -1) {
            route = `/wallcoverings/kids/desc/${routeIndex + 1}`;
          }
        } else if (title.startsWith('#MP')) {
          routeIndex = minimalPatternData.findIndex(i => i.title === title);
          if (routeIndex !== -1) {
            route = `/wallcoverings/minimal and pattern/desc/${routeIndex + 1}`;
          }
        } else if (title.startsWith('#EX')) {
          routeIndex = artprintsExclusiveData.findIndex(i => i.title === title);
          if (routeIndex !== -1) {
            route = `/artprints/exclusive/desc/${routeIndex + 1}`;
          }
        } else if (title.startsWith('#LS')) {
          routeIndex = artprintsLandscapeData.findIndex(i => i.title === title);
          if (routeIndex !== -1) {
            route = `/artprints/landscape/desc/${routeIndex + 1}`;
          }
        } else if (title.startsWith('#ST')) {
          routeIndex = artprintsStData.findIndex(i => i.title === title);
          if (routeIndex !== -1) {
            route = `/artprints/set of 2 and 3/desc/${routeIndex + 1}`;
          }
        } else if (title.startsWith('#EHP')) {
          // Exclusive hand paintings use 'code' field, and route uses the code number
          const codeNumber = parseInt(title.replace('#EHP', ''));
          if (!isNaN(codeNumber)) {
            route = `/exclusivehand/desc/${codeNumber}`;
            routeIndex = 0; // Set to 0 to indicate route was found
          } else {
            // Try finding in exclusiveHandData array
            routeIndex = exclusiveHandData.findIndex(i => (i.code || i.title) === title);
            if (routeIndex !== -1) {
              const code = exclusiveHandData[routeIndex].code || exclusiveHandData[routeIndex].title;
              const codeNumber = parseInt(code.replace('#EHP', ''));
              if (!isNaN(codeNumber)) {
                route = `/exclusivehand/desc/${codeNumber}`;
              }
            }
          }
        }
        
        // If not found in specific category data, check viewall data (for items without category prefixes)
        if (!route) {
          routeIndex = wallcoveringViewAll.findIndex(i => i.title === title);
          if (routeIndex !== -1) {
            route = `/wallcoverings/desc/${routeIndex + 1}`;
          } else {
            routeIndex = artprintsViewAll.findIndex(i => i.title === title);
            if (routeIndex !== -1) {
              route = `/artprints/desc/${routeIndex + 1}`;
            }
          }
        }
        
        // Log route determination for debugging
        if (!route || routeIndex === -1) {
          console.warn('Could not determine route for item:', title, 'routeIndex:', routeIndex);
        } else {
          console.log('Determined route for', title, ':', route);
        }
          
        return {
          ...item,
          route,
          category: getCategoryFromTitle(title),
          selections: selections, // Include selections in the item object
          wishlistId: itemId, // Preserve the full ID
        };
      })
      .filter(Boolean);
    
    console.log('Wishlist page - foundItems:', foundItems);
    setItems(foundItems);
  }, [wishlistItems, allDataSources]);

  const getCategoryFromTitle = (title) => {
    if (title.startsWith('#DC')) {
      // Check if it's in decorative wallcovering or artprint
      const isWallcovering = decorativeData.find(i => i.title === title);
      return isWallcovering ? 'Wallcovering - Decorative' : 'Art Print - Decorative';
    }
    if (title.startsWith('#AW')) return 'Wallcovering - Abstract';
    if (title.startsWith('#AB')) return 'Art Print - Abstract';
    if (title.startsWith('#CR')) return 'Wallcovering - Corporate';
    if (title.startsWith('#KD')) return 'Wallcovering - Kids';
    if (title.startsWith('#MP')) return 'Wallcovering - Minimal and Pattern';
    if (title.startsWith('#EX')) return 'Art Print - Exclusive';
    if (title.startsWith('#LS')) return 'Art Print - Landscape';
    if (title.startsWith('#ST')) return 'Art Print - Set of 2 and 3';
    if (title.startsWith('#EHP')) return 'Exclusive Hand Painting';
    return 'Other';
  };

  const handleShare = async () => {
    const url = getShareableUrl();
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      // Fallback: show URL in alert
      alert(`Share this URL:\n${url}`);
    }
  };

  const handleItemClick = (route, item) => {
    console.log('View Details clicked - route:', route, 'item:', item);
    if (route && route.trim() !== '') {
      // Validate route format
      if (route.startsWith('/') && route.includes('/desc/')) {
        navigate(route);
      } else {
        console.error('Invalid route format:', route);
        // Try to construct route from item title as fallback
        const title = item?.title;
        if (title) {
          console.log('Attempting to construct route from title:', title);
          // This is a fallback - the route should have been set correctly above
          alert('Unable to navigate to item details. Please try adding the item to wishlist again.');
        }
      }
    } else {
      console.error('Route is empty or undefined for item:', item);
      alert('Unable to navigate to item details. Route information is missing.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto pt-24 px-4 sm:px-6 lg:px-8 pb-16">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-3xl md:text-4xl font-serif text-gray-800 flex items-center gap-3">
              <Heart className="w-8 h-8 text-red-500 fill-red-500" />
              My Wishlist
            </h1>
            {items.length > 0 && (
              <div className="flex gap-3">
                <button
                  onClick={handleShare}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Share2 className="w-4 h-4" />
                      Share
                    </>
                  )}
                </button>
                <button
                  onClick={clearWishlist}
                  className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Clear All
                </button>
              </div>
            )}
          </div>
          <p className="text-gray-600">
            {items.length === 0 
              ? 'Your wishlist is empty. Start adding items you love!'
              : `You have ${items.length} item${items.length !== 1 ? 's' : ''} in your wishlist.`
            }
          </p>
        </div>

        {items.length === 0 ? (
          <div className="text-center py-20">
            <Heart className="w-24 h-24 text-gray-300 mx-auto mb-4" />
            <p className="text-xl text-gray-500 mb-4">No items in your wishlist yet</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Browse Products
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {items.map((item, index) => (
              <div
                key={`${item.title}-${index}`}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow group cursor-pointer"
              >
                <div className="relative aspect-square overflow-hidden bg-gray-100">
                  <img
                    src={item.Img || item.ImgJpg || item.ImgJpeg || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect fill="%23ddd" width="400" height="400"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="20" dy="10.5" font-weight="bold" x="50%25" y="50%25" text-anchor="middle"%3ELoading...%3C/text%3E%3C/svg%3E'}
                    alt={item.title || item.desc || 'Wishlist item'}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    onError={(e) => {
                      // Try fallback images in order
                      const currentSrc = e.target.src;
                      console.log('Image error for item:', item.title, 'currentSrc:', currentSrc, 'available images:', {
                        Img: item.Img,
                        ImgJpg: item.ImgJpg,
                        ImgJpeg: item.ImgJpeg
                      });
                      
                      // Try ImgJpg first if different from current
                      if (item.ImgJpg && currentSrc !== item.ImgJpg) {
                        console.log('Trying ImgJpg:', item.ImgJpg);
                        e.target.src = item.ImgJpg;
                      } 
                      // Then try ImgJpeg
                      else if (item.ImgJpeg && currentSrc !== item.ImgJpeg) {
                        console.log('Trying ImgJpeg:', item.ImgJpeg);
                        e.target.src = item.ImgJpeg;
                      } 
                      // Then try Img
                      else if (item.Img && currentSrc !== item.Img) {
                        console.log('Trying Img:', item.Img);
                        e.target.src = item.Img;
                      } 
                      // If all images fail, show placeholder
                      else {
                        console.log('All images failed, showing placeholder');
                        e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect fill="%23ddd" width="400" height="400"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="20" dy="10.5" font-weight="bold" x="50%25" y="50%25" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                      }
                    }}
                    onLoad={() => {
                      console.log('Image loaded successfully for item:', item.title);
                    }}
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFromWishlist(item.wishlistId || item.title);
                    }}
                    className="absolute top-2 right-2 p-2 bg-white/90 rounded-full hover:bg-white transition-colors group"
                    title="Remove from wishlist"
                  >
                    <Heart className="w-5 h-5 text-red-500 fill-red-500 group-hover:scale-110 transition-transform" />
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                    <p className="text-white font-semibold text-sm">{item.title}</p>
                    <p className="text-white/80 text-xs">{item.category}</p>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-gray-800 mb-1 line-clamp-2">{item.desc || item.title}</h3>
                  {item.context && (
                    <p className="text-sm text-gray-600 line-clamp-2 mb-2">{item.context}</p>
                  )}
                  
                  {/* Display selections if available */}
                  {item.selections && (
                    <div className="mb-3 p-2 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-xs font-semibold text-gray-700 mb-1">Selected Options:</p>
                      {item.selections.material && (
                        <p className="text-xs text-gray-600">Material: <span className="font-medium">{item.selections.material}</span></p>
                      )}
                      {item.selections.orientation && (
                        <p className="text-xs text-gray-600">Orientation: <span className="font-medium">{item.selections.orientation}</span></p>
                      )}
                      {item.selections.size && (
                        <p className="text-xs text-gray-600">Size: <span className="font-medium">{item.selections.size}</span></p>
                      )}
                      {item.selections.isCustom && item.selections.customWidth && item.selections.customHeight && (
                        <p className="text-xs text-gray-600">Custom: <span className="font-medium">{item.selections.customWidth}" × {item.selections.customHeight}"</span></p>
                      )}
                      {(item.selections.price || item.selections.calculatedPrice) && (
                        <p className="text-xs text-gray-600 mt-1">Price: <span className="font-medium text-green-600">₹{(item.selections.price || item.selections.calculatedPrice).toLocaleString('en-IN')}</span></p>
                      )}
                    </div>
                  )}
                  
                  <button
                    onClick={() => {
                      // Build route with selections as URL params
                      let routeWithSelections = item.route;
                      if (item.selections && item.route) {
                        const params = new URLSearchParams();
                        if (item.selections.material) params.set('material', item.selections.material);
                        if (item.selections.orientation) params.set('orientation', item.selections.orientation);
                        if (item.selections.size) params.set('size', item.selections.size);
                        if (item.selections.isCustom) {
                          params.set('mode', 'custom');
                          if (item.selections.customWidth) params.set('width', item.selections.customWidth);
                          if (item.selections.customHeight) params.set('height', item.selections.customHeight);
                        } else if (item.selections.material || item.selections.orientation || item.selections.size) {
                          params.set('mode', 'preset');
                        }
                        const queryString = params.toString();
                        if (queryString) {
                          routeWithSelections = `${item.route}?${queryString}`;
                        }
                      }
                      handleItemClick(routeWithSelections, item);
                    }}
                    disabled={!item.route || item.route.trim() === ''}
                    className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                      !item.route || item.route.trim() === ''
                        ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                        : 'bg-gray-600 text-white hover:bg-gray-700'
                    }`}
                  >
                    {item.route && item.route.trim() !== '' ? (
                      <>
                        View Details
                        <ExternalLink className="w-4 h-4" />
                      </>
                    ) : (
                      'Route unavailable'
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Scrollfunctions />
      <Footer />
    </div>
  );
};

export default Wishlist;
